import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SearchResultsListComponent } from '../search-results-list/search-results-list.component';
import { TreeNode } from 'primeng/api';
import { Commands, Results, Search, Wiki } from '@local/client-contracts';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { FolderItem, SearchResults, isWikiCard } from '../../../results';
import { TreeNodeDropEvent } from 'primeng/tree';
import { PopupRef, UTreeComponent } from '@local/ui-infra';
import { AppPopupComponent, AppPopupData } from 'src/app/bar/components/app-popup/app-popup.component';
import { stopEvent } from '@shared/utils/elements-util';
import { cloneDeep, isNil, omit } from 'lodash';
import { generateTitleUrl, isEnterKey } from '@local/ts-infra';
import { ToasterData } from 'src/app/bar/components/toaster/toaster-data';
import { getUrlByPreviewKey } from '../../../preview/helpers/preview.utils';
import { untilDestroyed } from '@ngneat/until-destroy';
import { filter, map } from 'rxjs';
import { SelectedItem } from '../wiki-collection/wiki-collection.component';
import { isNativeWindow } from '@local/common-web';
import { Config } from '@environments/config';

export type WikiFolder = FolderItem & { path: Wiki.Path[] };

@Component({
  selector: 'wiki-tree',
  templateUrl: './wiki-tree.component.html',
  styleUrls: ['./wiki-tree.component.scss'],
})
export class WikiTreeComponent extends SearchResultsListComponent implements OnInit, OnDestroy {
  private readonly NODE_CLASS = '.p-treenode';
  private readonly NODE_DATA_CLASS = 'p-treenode-label';
  private readonly WARNING_FOLDER_POPUP: AppPopupData = {
    showButtons: true,
    message: 'All cards within this folder will be <br> permanently deleted.',
    messageStyle: { fontSize: '12px' },
    content: {
      title: 'Are you sure you want to <br> delete this folder?',
      secondaryButton: 'Cancel',
      primaryButton: 'Delete',
    },
  };
  private readonly TOASTER_LIMIT_FOLDER_DATA: ToasterData = {
    id: 'limit-folder',
    title: 'This folder cannot be dragged because the subfolder limit has been exceeded.',
    defaultErrorIcon: true,
  };

  readonly isNative = isNativeWindow();

  private _tree: TreeNode[];
  private originalTree: TreeNode[];
  private flattenTree: TreeNode[];
  private warningDeletePostPopupRef: PopupRef<AppPopupComponent, AppPopupData>;
  private selectedFromFocus: boolean;
  private _showInputTitleIndex: number;
  private activeFolderId: string;
  private prevActualSelected = 0;
  selectedNodes: TreeNode[];

  @Input() canEdit: boolean;
  @Input() wikiId: string;
  @Input() highlights: string[];
  @Input() isSearchState: boolean;
  @Input() set tree(value: TreeNode[]) {
    this.initTree(value);
  }
  @Input() clearSelectedNodes: EventEmitter<boolean> = new EventEmitter();
  @Input() onCreatedFolder: EventEmitter<boolean> = new EventEmitter();

  set showInputTitleIndex(value: number) {
    this._showInputTitleIndex = value;
    if (this.showInputTitleIndex === 0) {
      this.setItemFocus('first');
    }
  }

  get tree(): TreeNode[] {
    return this._tree;
  }

  get showInputTitleIndex(): number {
    return this._showInputTitleIndex;
  }

  @Output() selectedItemsUpdate = new EventEmitter<SelectedItem[]>();

  @ViewChild('utree') treeRef: UTreeComponent;

  @HostBinding('style.--itemsWidth') get itemsWidth(): string {
    return `${this.canEdit ? 60 : 48}px`;
  }

  @HostBinding('class.selection-state')
  get isSelectionState() {
    return this.selectedNodes?.length;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.previewHandlerService.changeSelectedIndex = false;
    this.routerService.queryParams$
      .pipe(
        untilDestroyed(this),
        filter((params) => params[this.wikiCardsService.FOLDER_CARD_PARAM]),
        map((p) => p[this.wikiCardsService.FOLDER_CARD_PARAM])
      )
      .subscribe(async (id) => {
        this.activeFolderId = id;
        this.openActiveFolder();
      });
    this.clearSelectedNodes.pipe(untilDestroyed(this)).subscribe((v: any) => {
      if (v === true) {
        this.selectionChange([]);
      }
    });
    this.onCreatedFolder.pipe(untilDestroyed(this)).subscribe((v: any) => {
      if (v === true) {
        this.showInputTitleIndex = 0;
      }
    });
    this.collectionsService.onFocusChange$.pipe(untilDestroyed(this)).subscribe((v) => {
      if (v) {
        this.selectionChange([]);
      }
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    const tree = document.getElementById('utree');
    if (tree) {
      document.addEventListener('click', this.clickListener, true);
      tree.addEventListener('focus', this.focusListener, true);
      tree.addEventListener('keydown', this.keydownListener, true);
    }
    this.setItemFocus('first');
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    document.removeEventListener('click', this.clickListener, true);
  }

  private clickListener = (event: MouseEvent) => this.onClick(event);
  private focusListener = (event: FocusEvent) => this.onFocus(event);
  private keydownListener = (event) => this.onEnter(event);

  private initTree(value: TreeNode[]) {
    if (this.findDifferences(this._tree, value)) {
      return;
    }
    if (this._tree) {
      this.mergeExpandedProperty(this._tree, value);
    } else {
      this._tree = value;
    }
    this.flattenTree = this.wikisService.getFlattenTree(this.tree);

    if (!this.flattenTree) {
      return;
    }

    //update the item in the side preview
    let relevantIndex = this.isRelevantIndex(this.selectedIndex) ? this.selectedIndex : this.prevActualSelected;
    if (relevantIndex >= this.flattenTree?.length) {
      relevantIndex = this.flattenTree?.length - 1;
    }
    this.setSelected(relevantIndex, this.flattenTree[relevantIndex]?.data, null, true);
    this.selectionChange([]);
    this.openActiveFolder();
    if (!this.tree?.length) {
      this.previewService?.setPreviewState('none');
    }
  }

  findDifferences(arr1: TreeNode[], arr2: TreeNode[]): boolean {
    const omitBullets = (obj: any) => omit(obj, ['data.view.bullets', 'partialSelected']);

    const safeStringify = (obj: any) => {
      const seen = new WeakSet();
      return JSON.stringify(obj, function (key, value) {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return;
          }
          seen.add(value);
        }
        return value;
      });
    };

    const modifiedArr1 = safeStringify(cloneDeep(arr1?.map(omitBullets)));
    const modifiedArr2 = safeStringify(cloneDeep(arr2?.map(omitBullets)));

    return modifiedArr1 === modifiedArr2;
  }

  private setItemFocus(which: 'first' | 'last') {
    if (this.activeFolderId) {
      return;
    }
    setTimeout(() => {
      const nodes = document?.querySelectorAll(this.NODE_CLASS);
      if (!nodes?.length) {
        return;
      }
      const selectedNode = (which === 'first' ? nodes[0] : nodes[nodes.length - 1]) as HTMLElement;
      selectedNode?.focus();
    }, 100);
  }

  private openActiveFolder() {
    if (!this.activeFolderId) {
      return;
    }
    const index = this.flattenTree?.findIndex((n) => n.data?.id === this.activeFolderId);
    if (!this.isRelevantIndex(index)) {
      return;
    }
    const node = this.flattenTree[index];
    //To open all folder levels
    let levelFolders = node.data?.path?.length;
    if (levelFolders > 1) {
      for (let i = index; levelFolders > 0; i--) {
        if (this.flattenTree[i]?.leaf) {
          continue;
        }
        this.flattenTree[i].expanded = true;
        levelFolders--;
      }
    }
    node.expanded = true;
    this.setSelected(index, node);
    this.scrollToElementTree(index);
    (this.treeRef as any)?.cdr?.markForCheck();
    this.routerService.removeQueryParam(this.wikiCardsService.FOLDER_CARD_PARAM);
    this.activeFolderId = null;
  }

  private scrollToElementTree(index: number) {
    setTimeout(() => {
      const nodeElement = this.treeRef?.treeRef.el.nativeElement.querySelector(`[data-index="${index}"]`);
      if (nodeElement) {
        nodeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      }
    }, 100);
  }

  protected skipHeader(index: number, removeFocus?: boolean) {
    if (removeFocus || (this.removeFocus && !this.componentFocused)) {
      this.selectedIndex = -1;
    }
  }

  onTitleChanged(newTitle: string, item, index: number) {
    newTitle = newTitle?.trim();
    this.titleChanged.emit({ value: newTitle, item: item });
    const currentNode = this.flattenTree[index];
    if (currentNode && currentNode.data?.type === 'folder' && newTitle) {
      currentNode.data.title = newTitle;
      (this.treeRef as any)?.cdr?.markForCheck();
      this.cdr.markForCheck();
    }
  }

  onBlur() {
    if (this.showInputTitleIndex) {
      return;
    }
    this.setSelected(-1, null);
  }

  private onFocus(event: FocusEvent) {
    const target = event.target as HTMLElement;
    const card = target.closest('.tree-result-item');
    if (card) {
      const index = Number(card.getAttribute('data-index'));
      if (index !== this.selectedIndex) {
        this.setFocus(target);
      }
      return;
    }
    const { node, index } = this.getSelectedFromEvent(target);
    if (this.isRelevantIndex(index) && index !== this.selectedIndex && node) {
      this.selectedFromFocus = true;
      this.setSelected(index, node.data);
    }
  }

  private setFocus(target: HTMLElement) {
    const newEvent = target.closest(this.NODE_CLASS);
    (newEvent as any)?.focus();
  }

  private getSelectedFromEvent(target: HTMLElement): { node: TreeNode; index: number } {
    const nodeLabel = this.findChildByClassName(target, this.NODE_DATA_CLASS);
    const item = nodeLabel?.children?.[0]?.children?.[0];
    if (!item) {
      return { node: null, index: null };
    }
    const index = Number(item.getAttribute('data-index'));
    return { node: this.flattenTree?.[index], index };
  }

  private findChildByClassName(target: HTMLElement, className: string): HTMLElement | null {
    if (target.classList.contains(className)) {
      return target;
    }

    for (const child of Array.from(target.children)) {
      const found = this.findChildByClassName(child as HTMLElement, className);
      if (found) {
        return found;
      }
    }

    return null;
  }

  private onEnter(event) {
    if (!isEnterKey(event.key)) {
      return;
    }
    const { node, index } = this.getSelectedFromEvent(event.target);
    if (!node) {
      return;
    }
    stopEvent(event);
    if (isWikiCard(node.data)) {
      this.onInvoke({ command: this.buildPreviewCommand(node.data), invokerItem: node.data, trigger: 'keyboard' }, 'result');
    } else {
      this.openFolder(index, node, true);
    }
  }

  private onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const card = target?.closest('.isCard');
    const isPreviewVisible = this.previewHandlerService.splitAreaPreviewModel.visiblePreview;
    const clickActions =
      (target?.closest('.result-title-container') && !isPreviewVisible) ||
      target?.closest('.context-menu') ||
      target?.closest('.indicate-summary');
    if (card && !clickActions && (this.selectedFromFocus || isPreviewVisible)) {
      //This is for the problem that a wiki-card popup opens on the first click
      stopEvent(event);
      this.selectedFromFocus = false;
    } else if (!target?.closest(this.NODE_CLASS) && !target.closest('.card-footer-container')) {
      this.onBlur();
    }
  }

  private buildPreviewCommand(model: SearchResults): Commands.Preview<SearchResults> {
    return {
      type: 'preview',
      previewable: true,
      state: 'popup',
      model,
      selectedIndex: this.selectedIndex,
      pageType: this.pageType,
    };
  }

  protected registerKeyboardHandler() {
    if (this.keyHandlerId) return;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      if (this.isPreviewDirty()) return;
      this.keyboardOn = true;
      const key = keys[0];
      if (isNil(this.selectedIndex) || this.selectedIndex < 0)
        switch (key) {
          case 'ArrowDown':
            this.setItemFocus('first');
            break;
          case 'ArrowUp':
            this.setItemFocus('last');
            break;
        }
    }, 7);
    this.cdr.markForCheck();
    return;
  }

  getModel(model: Wiki.Card | Wiki.Folder) {
    return { ...model, highlights: this.highlights };
  }

  setSelected(index: number, item: any, data?: InvokeCommand, forceSelected?: boolean) {
    if (!forceSelected && index === this.selectedIndex) {
      return;
    }
    if (item || !this.isPreviewMode) {
      this.previewHandlerService.innerSelected = item;
    }
    if (this.isRelevantIndex(index)) {
      this.prevActualSelected = index;
    }
    super.updateSelectedIndex(index, data);
  }

  isRelevantIndex(index: number) {
    return !isNil(index) && index > -1;
  }

  protected getShowResultSections(item): Results.ShowResultSections {
    const showResultSections = super.getShowResultSections(item);
    showResultSections.showSingleLineView = this.isPreviewMode;
    return showResultSections;
  }

  private mergeExpandedProperty(originalTree: TreeNode[], newTree: TreeNode[]) {
    if (this.isSearchState) {
      this.originalTree = originalTree;
    } else if (this.originalTree) {
      originalTree = this.originalTree;
      this.originalTree = null;
    }
    // Create a map from originalTree for quick lookup of expanded property by data.id
    const expandedMap: { [key: string]: boolean } = {};
    if (originalTree) {
      const mapExpandedProperties = (nodes: TreeNode[]) => {
        nodes.forEach((node) => {
          if (node.expanded !== undefined) {
            expandedMap[node.data.id] = node.expanded;
          }
          if (node.children) {
            mapExpandedProperties(node.children);
          }
        });
      };
      mapExpandedProperties(originalTree);
    }

    // Function to merge the expanded property recursively
    const mergeNodes = (node: TreeNode): TreeNode => {
      const expanded = expandedMap[node.data.id];
      if (expanded || node.expanded) {
        node.expanded = true;
      }
      if (node.children) {
        node.children = node.children?.map(mergeNodes);
      }
      return node;
    };

    this._tree = newTree?.map(mergeNodes);
  }

  async nodeDrop(event: TreeNodeDropEvent) {
    const itemId = event.dragNode?.data?.id;
    const prevParentId = event?.dragNode?.parent?.data?.id || this.wikiId;
    const newParentId = event.dropNode?.data?.id || this.wikiId;
    let index = event.index;
    if (!this.isRelevantIndex(index)) {
      index = this.tree?.findIndex((node) => node.data?.id === itemId);
    }
    await this.wikisService.moveItem([{ itemId, prevParentId, newParentId, index }]);
  }

  validateDrop(current, newParent): boolean {
    return current?.data?.type !== 'folder' || (newParent?.data?.path.length || 0) + current?.data?.countSubLevels < 3;
  }

  onCancelDrop() {
    this.wikisService.showToaster(this.TOASTER_LIMIT_FOLDER_DATA);
  }

  selectionChange(selectedNodes: TreeNode | TreeNode[]) {
    this.selectedNodes = Array.isArray(selectedNodes) ? selectedNodes : [selectedNodes];
    this.selectedItemsUpdate.emit(
      this.selectedNodes?.map((node) => {
        const path = node?.data?.path;
        return {
          id: node?.data?.id,
          parentId: path?.[path?.length - 1]?.id,
          path,
          type: node?.data?.type,
          countSubLevels: node?.data?.countSubLevels,
        };
      }) || []
    );
    this.cdr.markForCheck();
  }

  openFolder(index: number, node: TreeNode, forceOpen?: boolean) {
    if (index !== this.selectedIndex || !isNil(this.showInputTitleIndex)) {
      return;
    }
    if (this.selectedFromFocus && !forceOpen) {
      this.selectedFromFocus = false;
      return;
    }
    node.expanded = !node.expanded;
    (this.treeRef as any)?.cdr?.markForCheck();
    this.cdr.markForCheck();
  }

  async onInvoke(data: InvokeCommand, type: Search.ResultType): Promise<void> {
    if (type === 'result') {
      return super.onInvoke(data, type);
    }
    const folder = data.invokerItem as WikiFolder;
    if (!folder) {
      return;
    }
    if (type === 'folder') {
      switch (data.command.type) {
        case 'copy-clipboard': {
          this.copyFolderUrl(folder.id);
          break;
        }
        case 'move-folder':
          this.collectionsService.openSelectPopup$.next({
            data: null,
            type: 'move-to-wiki-popup',
            options: { doSetup: true, data: { command: data.command, context: { item: data.invokerItem } } },
          });
          break;
        case 'create-card': {
          const purl = getUrlByPreviewKey('a', { name: folder.title, id: 'new' });
          this.routerService.addQueryParam({ purl, parentId: folder.id });
          break;
        }
        case 'create-folder':
          await this.createSubFolder(folder);
          break;
        case 'rename-folder':
          this.showInputTitleIndex = folder.resultIndex;
          this.cdr.markForCheck();
          break;
        case 'delete-folder':
          this.openDeleteFolderWarningPopup(folder.id);
          break;
      }
    }
  }

  private copyFolderUrl(folderId: string) {
    const wiki = this.collectionsService.getCollectionById(this.wikiId);
    if (!wiki) {
      return;
    }
    const wikiUrl = generateTitleUrl('wikis', wiki.title, wiki.id);
    return this.commandsService.executeCommand({
      type: 'copy-clipboard',
      value: `${Config.baseUrl}/${wikiUrl}?${this.wikiCardsService.FOLDER_CARD_PARAM}=${folderId}`,
    } as Commands.CopyClipboard);
  }

  async createSubFolder(folder: WikiFolder) {
    await this.wikisService.createFolder({ wikiId: this.wikiId, parentId: folder.id });
    const index = folder.resultIndex;
    const node = this.flattenTree[index];
    if (node) {
      node.expanded = true;
    }
    this.setSelected(index + 1, null);
    this.showInputTitleIndex = index + 1;
    (this.treeRef as any)?.cdr?.markForCheck();
    this.cdr.markForCheck();
  }

  openDeleteFolderWarningPopup(folderId: string) {
    if (this.warningDeletePostPopupRef) {
      this.closeWarningPopup();
    }
    this.warningDeletePostPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>(
      'center',
      AppPopupComponent,
      this.WARNING_FOLDER_POPUP,
      {
        position: 'center',
      }
    );
    this.warningDeletePostPopupRef.compInstance.primaryButton.subscribe(() => {
      this.closeWarningPopup();
      this.wikisService.deleteWikiItem({ itemId: folderId });
      this.selectionChange([]);
    });
    this.warningDeletePostPopupRef.compInstance.secondaryButton.subscribe(() => {
      this.closeWarningPopup();
    });
  }

  closeWarningPopup() {
    this.warningDeletePostPopupRef.destroy();
    this.warningDeletePostPopupRef = null;
  }

  //reset selection
  openContextMenu(index: number, item: SearchResults) {
    super.openContextMenu(index, item);
    this.changeFocus('card-context-menu');
  }

  changeFocus(focused: string) {
    this.collectionsService.onFocusChange = focused;
  }
}
